import { useEffect, useImperativeHandle, useMemo, useRef, useState, type Ref } from "react";
import { tweenPan, type MapCamera } from "@/shared/map/mapCamera";
import { mercatorY } from "@/shared/map/projection";
import { Icon } from "@/shared/ui";
import type { GlobeCluster, PlanetGlobeProps } from "./globeTypes";
import { IDENTITY_VIEW, Map2D, type MapView, type Project } from "./Map2D";
import { cityLabels, citiesOf, clustersOf, dotGroups, isMergedKey, mergedCluster, mergedKeys, placeText } from "./publicMapModel";
import { clusterNames, CountDot } from "./SpotMarker";

/**
 * 星球总览的 2D 占位（props 与 planet3d/PlanetGlobe 一模一样，见 ./globeTypes）：
 * 3D 星球（6796）交来之前、WebGL 不可用、或系统要求减少动态效果时用它。
 * - 同坐标聚合（clustersOf，按坐标相等），每处一个人数点；点了调 onSelect，人多的那一格由页面弹名单和短卡（这里不扇出）；
 *   远看几处会叠在一起：屏幕上压住的几处合成一个点（第四轮，c84a D0842-2 点按区不盖点；不挪坐标，画在人最多那一处），
 *   写一共几位，点开是这几位的名单；拉近分得开了就各自显示；
 * - “放大看这一带”调 onRegion：选中了哪处就以它为中心，没选就以眼下镜头中心；跨度用 regionSpanDeg（缺省 25 度，和 3D 组件一样）；
 * - 示意图：Web Mercator 直接投 WGS-84 坐标（不是真实底图），图上写明；能拖动、缩放；镜头记在 sessionStorage（访客自己的 public 作用域），回来接着看。
 *   view / onViewChange（3D 的镜头）、textureUrl 占位里不用；
 * - highlights：刚发生事件的地方闪一圈（public-map.css 的 .ps-pmap-pulse；减少动态效果时不闪，只留一圈静止的描边）。
 */
const VIEW_KEY = "petsoul:public-world:v1:fallback-view";

function readView(): MapView {
  try {
    const raw = window.sessionStorage.getItem(VIEW_KEY);
    const v: unknown = raw ? JSON.parse(raw) : null;
    if (v && typeof v === "object") {
      const { scale, tx, ty } = v as Record<string, unknown>;
      if ([scale, tx, ty].every((n) => typeof n === "number" && Number.isFinite(n)) && (scale as number) > 0) return { scale: scale as number, tx: tx as number, ty: ty as number };
    }
  } catch {
    /* 读不到：从总览开始 */
  }
  return IDENTITY_VIEW;
}

/** 屏幕坐标换回经纬度：用投影在两个已知点上的读数解出线性关系（Web Mercator 在经度、墨卡托纬度上都是线性的）。 */
function unproject(project: Project, x: number, y: number): { lat: number; lng: number } {
  const a = project({ lat: 0, lng: 0 });
  const b = project({ lat: 10, lng: 10 });
  const lng = ((x - a.x) / (b.x - a.x)) * 10;
  const my = ((y - a.y) / (b.y - a.y)) * mercatorY(10);
  const lat = (2 * Math.atan(Math.exp(my)) - Math.PI / 2) * (180 / Math.PI);
  return { lat, lng };
}

/** camera：就近面板放不下生活照时，地图用它把被点的那一位轻轻往上挪（117-2b，见 shared/map/mapCamera）；3D 星球那边没有这一项 */
export function GlobeFallback({ residents, coordSystem, onSelect, onRegion, selectedKey = null, regionSpanDeg = 25, label, highlights = [], camera }: PlanetGlobeProps & { camera?: Ref<MapCamera> }) {
  const clusters = useMemo(() => clustersOf(residents), [residents]);
  const bounds = useMemo(() => clusters.map((cluster) => ({ lat: cluster.lat, lng: cluster.lng })), [clusters]);
  const [view, setView] = useState<MapView>(readView);
  const stopPan = useRef<() => void>(() => {});
  useImperativeHandle(camera, () => ({
    panBy: (dx: number, dy: number, instant: boolean) => {
      stopPan.current();
      stopPan.current = tweenPan((x, y) => setView((v) => ({ ...v, tx: v.tx + x, ty: v.ty + y })), dx, dy, instant);
    },
  }), []);
  useEffect(() => () => stopPan.current(), []);
  useEffect(() => {
    try {
      window.sessionStorage.setItem(VIEW_KEY, JSON.stringify(view));
    } catch {
      /* 存不下：下次从总览开始 */
    }
  }, [view]);
  // 选中的是合成的那一处时，以画标记的那一处（组里第一处、人最多）为准
  const selectedAnchor = selectedKey ? mergedKeys(selectedKey)[0] : null;
  const selected = clusters.find((cluster) => cluster.key === selectedAnchor) ?? null;
  const dotLabel = (cluster: GlobeCluster) => {
    const cities = [...new Set(cluster.residents.map((r) => r.city?.trim()).filter((c): c is string => Boolean(c)))];
    const city = cities.length === 1 ? cities[0] : null;
    // 4b（I 10-14）：跨了几座城的那一盏，点开先按城选（SpotChooser）
    if (isMergedKey(cluster.key) && cities.length >= 2) return `这一带挨得很近的 ${cluster.residents.length} 位，在${citiesOf(cluster.residents).cities.map((c) => c.city).join("、")}：${clusterNames(cluster)}。点开先选城，放大也能分开看`;
    if (isMergedKey(cluster.key)) return `${city ? `${city}一带` : "这一带"}挨得很近的 ${cluster.residents.length} 位：${clusterNames(cluster)}。点开选一位，放大能分开看`;
    return cluster.residents.length === 1 ? `${clusterNames(cluster)}：${placeText(cluster.residents[0]!)}` : `${city ? `${city}一带` : "这一处"}有 ${cluster.residents.length} 位：${clusterNames(cluster)}`;
  };
  return (
    <div className="ps-pmap__layer" data-layer="overview" data-testid="globe-fallback">
      <Map2D bounds={bounds} view={view} onView={setView} showZoomControls={false} label={`${label}（示意图，可拖动、缩放；方向键移动，加号减号缩放）`}>
        {(project, size) => (
          <>
            <div className="ps-pmap__labels" aria-hidden="true">
              {cityLabels(clusters).map((city) => {
                const p = project(city.at);
                return (
                  <span key={city.city} className="ps-pmap__city" style={{ transform: `translate(${p.x}px, ${p.y}px)` }}>
                    {city.city}
                  </span>
                );
              })}
            </div>
            <div className="ps-pmap__labels" aria-hidden="true" data-testid="globe-highlights">
              {highlights.map((h) => {
                const p = project(h);
                return <span key={`${h.key}:${h.at}`} className="ps-pmap-pulse" data-key={h.key} style={{ transform: `translate(${p.x}px, ${p.y}px)` }} />;
              })}
            </div>
            <ul className="ps-pmap__markers" aria-label="地图上的居民（每处的人数）">
              {/* 屏幕上会压住的几处合成一个点（第四轮）：压在下面的点既看不见也点不到；拉近分得开了就各自显示 */}
              {dotGroups(clusters, project).map(mergedCluster).map((cluster) => {
                const p = project(cluster);
                return (
                  <li key={cluster.key} className="ps-pmap__at is-center" style={{ transform: `translate(${p.x}px, ${p.y}px)`, zIndex: cluster.residents.length }}>
                    <CountDot cluster={cluster} label={dotLabel(cluster)} selected={cluster.key === selectedKey} onSelect={onSelect} />
                  </li>
                );
              })}
            </ul>
            {clusters.length ? (
              <button
                type="button"
                className="ps-pmap__zoomin"
                data-map-interactive
                onClick={() => {
                  const center = selected ? { lat: selected.lat, lng: selected.lng } : unproject(project, size.width / 2, size.height / 2);
                  onRegion({ center, latSpan: regionSpanDeg, lngSpan: regionSpanDeg, coordSystem });
                }}
              >
                <Icon name="plus" size={16} /> {selected ? "放大看这一处附近" : "放大看这一带"}
              </button>
            ) : null}
          </>
        )}
      </Map2D>
      <p className="ps-pmap__attribution">示意图 · 住宅按登记小区；片区大约；上次非此刻</p>
    </div>
  );
}
